import { Grid, Typography, Divider, Button, Box } from '@mui/material';
import { OpenInNewOutlined, CheckCircle, Cancel, ReplayCircleFilled } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/shared/functions/formatDate';
import { useSelector } from 'react-redux';
import { RootState } from 'src/shared/services/store';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { FAILED_STATUSES, PaymentsStatusEnum } from 'src/shared/models/enums/paymentsStatus.enum';
import { InvoicePayment } from '../models/invoicePayment';
import { Invoice } from '../models/invoice';

type InvoiceMissionDetailsProps = {
  balance: number;
  invoice: Invoice;
};

const InvoiceMissionDetails = (props: InvoiceMissionDetailsProps) => {
  const { balance, invoice } = props;
  const { mission, payments } = invoice;

  const { t } = useTranslation(['invoice']);
  const user = useSelector((state: RootState) => state.oidc.user);

  const FSPayLink = `${process.env.REACT_APP_FS_PAY_URL}/${invoice.invoice_id}/${invoice.invoiceGuid}/${user?.profile.custname}`;

  return (
    <ContentSection header={t('invoice:payment.paymentInformation')}>
      {mission ? (
        <>
          <Grid container item xs={12}>
            <Grid xs={12} item>
              <Typography sx={{ fontWeight: 600, fontSize: 16 }} variant='h6'>
                {t('invoice:payment.payee')}:
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Typography sx={{ wordBreak: 'break-word' }}>{mission.senderName}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid xs={12} item>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 2 }} variant='h6'>
                {t('invoice:payment.paymentAccount')}:
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Typography>{mission.accountNr}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid xs={12} item>
              <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 2 }} variant='h6'>
                {t('invoice:payment.balance')}:
              </Typography>
            </Grid>
            <Grid xs={12} item>
              <Typography>
                <AmountWithCurrency amount={balance} />
              </Typography>
            </Grid>
          </Grid>
          {invoice.invoiceGuid && user && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<OpenInNewOutlined />}
                href={FSPayLink}
                target='_blank'
                rel='noopener noreferrer'
                fullWidth
              >
                {t('invoice:payment.paymentLink')}
              </Button>
            </Grid>
          )}
          {payments && payments?.length > 0 && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
            </Grid>
          )}
          <Grid item xs={12} flexDirection='column'>
            {payments &&
              payments.map((payment: InvoicePayment) => {
                const isRenewed = payment.status === PaymentsStatusEnum.AgWillRetry;
                const isFailed = payment.status != null && FAILED_STATUSES.includes(payment.status as PaymentsStatusEnum) && !isRenewed;
                return (
                  <Box key={payment.invoicePayment_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    {(() => {
                      if (isRenewed) return <ReplayCircleFilled sx={{ color: '#ff9800', fontSize: 20, flexShrink: 0 }} />;
                      if (isFailed) return <Cancel sx={{ color: '#f44336', fontSize: 20, flexShrink: 0 }} />;
                      return <CheckCircle sx={{ color: '#00af83', fontSize: 20, flexShrink: 0 }} />;
                    })()}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box display='flex' alignItems='center' justifyContent='space-between'>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {(() => {
                            if (isRenewed) return t('invoice:payment.renewedCoverage');
                            if (isFailed) return t('invoice:payment.failedPayment');
                            return t('invoice:payment.paymentRegistratedThrough');
                          })()}{' '}
                          {payment.source}
                          {(isFailed || isRenewed) && ' (autogiro)'}
                          {!isFailed && !isRenewed && (
                            <>
                              , <AmountWithCurrency amount={payment.amount} />
                            </>
                          )}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                          {formatDate(payment.date)}
                        </Typography>
                      </Box>
                      {(isFailed || isRenewed) && payment.statusMessage && (
                        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.2 }}>
                          {payment.statusMessage}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
          </Grid>
        </>
      ) : (
        <Typography>{t('invoice:payment.noPaymentInfo')}</Typography>
      )}
    </ContentSection>
  );
};

export default InvoiceMissionDetails;
